
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { FormData, Calculations } from './types';
import { calculateResults } from './utils';
import { Send, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import UniversalPrompt from "@/components/shared/UniversalPrompt";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface RatePromptProps {
  onResultsCalculated: (formData: FormData, calculations: Calculations) => void;
}

export const RatePrompt = ({ onResultsCalculated }: RatePromptProps) => {
  const [prompt, setPrompt] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(false);
  const [extractedData, setExtractedData] = useState<FormData | null>(null);
  const { toast } = useToast();

  const examples = [
    "I earn $85,000 a year with a $5,000 bonus. I spend about $150 monthly on internet and tech, $250 on office space, and take 15 days off a year.",
    "I'm a senior developer making $120k with 3 weeks vacation and $500 monthly expenses for software and equipment.",
    "My income is $95,000, I take 20 days off yearly, and spend around $300 monthly on work-related expenses."
  ];

  const handlePromptSubmit = async (promptText: string) => {
    if (!promptText.trim()) {
      toast({
        description: "Please enter some details about your situation.",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    setPrompt(promptText);
    
    // Simulate AI processing time
    setTimeout(() => {
      try {
        // This is where we would normally process the prompt with an AI
        const extractedFormData = extractDataFromPrompt(promptText);
        setExtractedData(extractedFormData);
        
        const calculations = calculateResults(extractedFormData);
        
        setIsAnalyzing(false);
        onResultsCalculated(extractedFormData, calculations);
        
        toast({
          title: "Analysis complete!",
          description: "We've calculated your rate based on your description.",
        });
      } catch (error) {
        setIsAnalyzing(false);
        toast({
          title: "Analysis failed",
          description: "We couldn't process your description. Please try again with more details.",
          variant: "destructive"
        });
      }
    }, 1500);
  };

  const extractDataFromPrompt = (promptText: string): FormData => {
    // Enhanced extraction logic
    const text = promptText.toLowerCase();
    
    // Extract salary (looking for common patterns)
    const salaryMatch = text.match(/\$?(\d{1,3}(,\d{3})*(\.\d+)?k)|(\$?\d{1,3}(,\d{3})*(\.\d+)?\s?(thousand|k|grand))|earn\s?\$?(\d{1,3}(,\d{3})*(\.\d+)?)|salary\s?\$?(\d{1,3}(,\d{3})*(\.\d+)?)|making\s?\$?(\d{1,3}(,\d{3})*(\.\d+)?)/i);
    let salary = "";
    if (salaryMatch) {
      // Clean up and standardize the salary
      const matchText = salaryMatch[0];
      salary = matchText.replace(/[^\d]/g, '');
      
      // Handle K notation (e.g., 85k, 120k)
      if (matchText.toLowerCase().includes('k') || matchText.toLowerCase().includes('thousand')) {
        salary = String(parseInt(salary) * 1000);
      }
    }
    
    // Extract bonus (if mentioned)
    const bonusMatch = text.match(/bonus\s?\$?(\d{1,3}(,\d{3})*(\.\d+)?)/i);
    let bonus = "";
    if (bonusMatch) {
      bonus = bonusMatch[0].replace(/[^\d]/g, '');
    }
    
    // Extract vacation days with enhanced patterns
    const vacationPatterns = [
      /(\d+)\s?(day|days|vacation|time off|off|pto)/i,
      /(\d+)\s?week(s)?\s?(vacation|time off|off|pto)/i,
      /take\s?(\d+)\s?(day|days|vacation|time off|off|pto)/i
    ];
    
    let vacation = "10"; // Default
    for (const pattern of vacationPatterns) {
      const match = text.match(pattern);
      if (match) {
        // Convert weeks to days if needed
        if (match[0].includes('week')) {
          vacation = String(parseInt(match[1]) * 5);
        } else {
          vacation = match[1];
        }
        break;
      }
    }
    
    // Extract monthly expenses with enhanced patterns
    const expensePatterns = [
      /\$?(\d{1,3}(,\d{3})*(\.\d+)?)\s?(month|monthly|on)\s?(expense|cost)/i,
      /spend\s?\$?(\d{1,3}(,\d{3})*(\.\d+)?)\s?(month|monthly|on)/i,
      /\$?(\d{1,3}(,\d{3})*(\.\d+)?)\s?for\s?(expense|cost)/i
    ];
    
    let totalMonthlyExpense = "200"; // Default
    for (const pattern of expensePatterns) {
      const match = text.match(pattern);
      if (match) {
        totalMonthlyExpense = match[1].replace(/[^\d]/g, '');
        break;
      }
    }
    
    // Distribute total expense across categories
    const expenseAmount = parseInt(totalMonthlyExpense) || 200;
    const internetPhone = Math.round(expenseAmount * 0.25).toString();
    const techSoftware = Math.round(expenseAmount * 0.25).toString();
    const workspaceOffice = Math.round(expenseAmount * 0.2).toString();
    const professionalDev = Math.round(expenseAmount * 0.1).toString();
    const healthcare = Math.round(expenseAmount * 0.1).toString();
    const transportation = Math.round(expenseAmount * 0.1).toString();
    
    // Create simplified form data based on extracted information
    return {
      salary: salary || "75000", // Default
      bonus: bonus || "",
      expenses: {
        internetPhone,
        techSoftware,
        workspaceOffice,
        professionalDev,
        healthcare,
        transportation,
      },
      timeOff: {
        vacation: vacation || "10",
        sick: "5",
        holidays: "10",
      },
      productivityRate: "80"
    };
  };

  if (isAnalyzing) {
    return (
      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2 animate-pulse">
          <Sparkles className="h-5 w-5 text-primary" />
          <p className="font-medium">Analyzing your information...</p>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
        <div className="mt-4 bg-muted p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">"{prompt}"</p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <UniversalPrompt
        title="Describe your work situation"
        description="Tell us about your income, expenses, and time off in your own words."
        placeholder="Example: I earn $85,000 a year with a $5,000 bonus. I spend about $150 monthly on internet and tech, $250 on office space, and take 15 days off a year."
        examples={examples}
        onSubmit={handlePromptSubmit}
      />

      {extractedData && (
        <Collapsible 
          open={isCollapsibleOpen} 
          onOpenChange={setIsCollapsibleOpen}
          className="mt-6"
        >
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full">
              {isCollapsibleOpen ? "Hide extracted data" : "Show extracted data"}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4">
            <Card className="p-4 space-y-4">
              <div>
                <h3 className="text-sm font-medium">Income</h3>
                <p className="text-sm text-muted-foreground">
                  Salary: ${parseInt(extractedData.salary).toLocaleString()}
                  {extractedData.bonus && ` + $${parseInt(extractedData.bonus).toLocaleString()} bonus`}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium">Monthly Expenses</h3>
                <p className="text-sm text-muted-foreground">
                  ${Object.values(extractedData.expenses).reduce((sum, val) => sum + (parseInt(val) || 0), 0).toLocaleString()} per month
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium">Time Off</h3>
                <p className="text-sm text-muted-foreground">
                  {extractedData.timeOff.vacation} vacation days, {extractedData.timeOff.sick} sick days, {extractedData.timeOff.holidays} holidays
                </p>
              </div>
            </Card>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};
